import React, { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Globe } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import BackToHome from "../components/BackToHome";

const FIELDS = ["intro", "decoration", "food", "dessert", "tradition", "table", "curiosity"];

export default function World() {
  const { t, lang } = useI18n();
  const [countries, setCountries] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => { api.get(`/christmas/world?lang=${lang}`).then(({ data }) => setCountries(data.countries)).catch(() => {}); }, [lang]);

  const open = async (code) => {
    const { data } = await api.get(`/christmas/world/${code}?lang=${lang}`);
    setDetail(data);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (detail) {
    return (
      <div data-testid="world-detail">
        <BackToHome crumbs={[{ label: t("world.title") }]} current={detail.name} />
        <button onClick={() => setDetail(null)} className="mb-5 inline-flex items-center gap-1 text-sm text-xmas-slate hover:text-xmas-snow"><ArrowLeft className="h-4 w-4" /> {t("world.back")}</button>
        <div className="mb-6 flex items-center gap-3">
          <span className="text-4xl">{detail.flag}</span>
          <h1 className="font-display text-3xl text-xmas-snow sm:text-4xl">{detail.name}</h1>
        </div>
        <p className="mb-6 max-w-2xl font-serif-elegant text-lg italic text-xmas-champagne" data-testid="world-field-intro">{detail.intro}</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {FIELDS.filter((f) => f !== "intro").map((f) => (
            <div key={f} className="glass rounded-2xl p-5" data-testid={`world-field-${f}`}>
              <p className="mb-1 text-xs uppercase tracking-widest text-xmas-gold">{t(`world.${f}`)}</p>
              <p className="text-sm text-xmas-snow">{detail[f]}</p>
            </div>
          ))}
        </div>
        {!detail.localized && <p className="mt-6 text-center text-xs text-xmas-slate">{t("world.note")}</p>}
      </div>
    );
  }

  return (
    <div data-testid="world-page">
      <BackToHome />
      <div className="float-up mb-8 flex items-center gap-3">
        <Globe className="h-7 w-7 text-xmas-gold" />
        <div>
          <h1 className="font-display text-3xl font-bold text-xmas-snow sm:text-4xl">🌍 {t("world.title")}</h1>
          <p className="mt-1 max-w-2xl text-xmas-champagne">{t("world.sub")}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {countries.map((c, i) => (
          <button key={c.code} onClick={() => open(c.code)} data-testid={`world-country-${c.code}`}
            className="card-lift group rounded-2xl border border-xmas-gold/15 bg-xmas-navycard/70 p-6 text-left transition" style={{ animationDelay: `${i * 0.05}s` }}>
            <span className="text-4xl">{c.flag}</span>
            <h3 className="mt-3 font-display text-xl text-xmas-snow">{c.name}</h3>
            <p className="mt-1 line-clamp-2 text-sm text-xmas-slate">{c.intro}</p>
            <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-xmas-gold">{t("world.explore")} <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-1" /></span>
          </button>
        ))}
      </div>
    </div>
  );
}
